@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_sys

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.example.project.enginemodule.SHOP_PURCHASED_KEY
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private const val KUNDENABRECHNUNG_MWST_SATZ = 0.19

private fun emptyModuleStats() = All(
    id = "module_stats",
    count = 0,
    wert = "0"
)

private fun loadVerbrauchModuleStats(entries: List<All>): All {
    val ownedRows = entries.filter {
        it.modulId > 0 && (it.shopPurchased || it.modulInstalliert)
    }

    return All(
        id = "module_stats",
        count = ownedRows.size,
        wert = ownedRows.count { it.modulInstalliert }.toString()
    )
}

private fun snapshotValue(container: All?, key: String): String =
    when (key) {
        "gesamtbetragEuro" -> container?.gesamtbetragEuro.orEmpty()
        "onboardingPreis" -> container?.onboardingPreis.orEmpty()
        "lizensierungPreis" -> container?.lizensierungPreis.orEmpty()
        "crStd" -> container?.crStd.orEmpty()
        "crKosten" -> container?.crKosten.orEmpty()
        "supportTickets" -> container?.supportTickets.orEmpty()
        "supportKosten" -> container?.supportKosten.orEmpty()
        "meetingStd" -> container?.meetingStd.orEmpty()
        "meetingKosten" -> container?.meetingKosten.orEmpty()
        "schulungStd" -> container?.schulungStd.orEmpty()
        "schulungKosten" -> container?.schulungKosten.orEmpty()
        "beratungStd" -> container?.beratungStd.orEmpty()
        "beratungKosten" -> container?.beratungKosten.orEmpty()
        else -> ""
    }

private fun abrechnungParseBetrag(value: String): Double {
    val normalized = value.trim()
        .replace("EUR", "", ignoreCase = true)
        .replace("€", "")
        .replace(".", "")
        .replace(",", ".")
        .trim()
    return normalized.toDoubleOrNull() ?: 0.0
}

private fun abrechnungFormatBetrag(value: Double): String =
    (js("new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format")(value) as String)

private fun abrechnungNetto(container: All?): Double =
    abrechnungParseBetrag(snapshotValue(container, "gesamtbetragEuro"))

private fun abrechnungMwst(container: All?): Double =
    abrechnungNetto(container) * KUNDENABRECHNUNG_MWST_SATZ

private fun abrechnungBrutto(container: All?): Double =
    abrechnungNetto(container) + abrechnungMwst(container)

private fun detailTypLabel(row: All): String = when {
    row.notiz.startsWith("AUTO_ONBOARDING_SETUP") -> "Onboarding"
    row.notiz.startsWith("AUTO_LIZENZ_AKTIV") -> "Lizenzierung"
    row.ticketnummer.isNotBlank() || row.notiz.startsWith("AUTO_SUPPORT_TICKET:") -> "Support"
    row.notiz.startsWith("AUTO_SERVICE_TERMIN:") -> row.terminArt.ifBlank { "Service" }
    row.notiz.isNotBlank() -> row.notiz
    else -> "Manuell"
}

private fun kundenabrechnungBillingMonthKey(): String {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    val year = if (now.monthNumber == 1) now.year - 1 else now.year
    val month = if (now.monthNumber == 1) 12 else now.monthNumber - 1
    return "${year}_${month.toString().padStart(2, '0')}"
}

@Composable
fun Sys_Kundenabrechnung() {
    var kunden by remember { mutableStateOf<List<All>>(emptyList()) }
    var verbrauchByKunde by remember { mutableStateOf<Map<String, All>>(emptyMap()) }
    var moduleStatsByKunde by remember { mutableStateOf<Map<String, All>>(emptyMap()) }
    var selectedKundeId by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    val monthKey = remember { kundenabrechnungBillingMonthKey() }

    suspend fun loadKundenFromNetzwerk(): List<All> {
        val lizByFirmenId = KundenverbrauchApi.loadAllDetails()
            .associateBy { it.firmenId.ifBlank { it.id } }

        return KundenverbrauchApi.loadCompanies()
            .filter { it.istkunde }
            .sortedBy { it.firma.lowercase() }
            .map { firma ->
                val firmenId = firma.firmenId.ifBlank { firma.id }
                val settings = lizByFirmenId[firmenId]
                firma.copy(
                    id = firmenId,
                    firmenId = firmenId,
                    lizenzkey = settings?.lizenzkey.orEmpty(),
                    nodePort = settings?.nodePort.orEmpty(),
                    rechnungsdatum = settings?.rechnungsdatum.orEmpty().ifBlank { "1" },
                    zustand = if (settings?.gesperrt == true) ZustandManager.Kunde.GESPERRT else firma.zustand
                )
            }
    }

    suspend fun loadMonatsverbrauch(kundenListe: List<All>): Map<String, All> {
        return kundenListe.associate { kunde ->
            val loaded = KundenverbrauchApi.loadVerbrauch(kunde.firmenId, monthKey)
            kunde.id to (loaded ?: All(
                id = "rechnung_${monthKey}_${kunde.firmenId}",
                firmenId = kunde.firmenId,
                firma = kunde.firma,
                datum = monthKey,
                verbrauch = emptyList()
            )).copy(
                firmenId = kunde.firmenId,
                firma = kunde.firma,
                lizenzkey = loaded?.lizenzkey.orEmpty(),
                datum = monthKey
            )
        }
    }

    suspend fun loadModuleStats(kundenListe: List<All>): Map<String, All> {
        val lizByFirmenId = KundenverbrauchApi.loadAllDetails()
            .associateBy { it.firmenId.ifBlank { it.id } }

        return kundenListe.associate { kunde ->
            val stats = lizByFirmenId[kunde.firmenId]
                ?.firmenId
                ?.takeIf { it.isNotBlank() }
                ?.let { loadVerbrauchModuleStats(KundenverbrauchApi.loadModules(it)) }
                ?: emptyModuleStats()

            kunde.id to stats
        }
    }

    fun selectedRows(): List<All> =
        verbrauchByKunde[selectedKundeId.orEmpty()]
            ?.verbrauch
            .orEmpty()
            .sortedWith(
                compareBy<All> { it.datum }
                    .thenBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
                    .thenBy { it.id }
            )

    fun mapKundeRow(kunde: All): List<String> = listOf(
        verbrauchByKunde[kunde.id]?.id.orEmpty().ifBlank { "${kunde.firmenId}_$monthKey" },
        kunde.firmenId,
        kunde.firma,
        kunde.lizenzkey,
        kunde.rechnungsdatum.ifBlank { "1" },
        monthKey,
        snapshotValue(verbrauchByKunde[kunde.id], "onboardingPreis"),
        snapshotValue(verbrauchByKunde[kunde.id], "lizensierungPreis"),
        snapshotValue(verbrauchByKunde[kunde.id], "crStd"),
        snapshotValue(verbrauchByKunde[kunde.id], "crKosten"),
        snapshotValue(verbrauchByKunde[kunde.id], "supportTickets"),
        snapshotValue(verbrauchByKunde[kunde.id], "supportKosten"),
        snapshotValue(verbrauchByKunde[kunde.id], "meetingStd"),
        snapshotValue(verbrauchByKunde[kunde.id], "meetingKosten"),
        snapshotValue(verbrauchByKunde[kunde.id], "schulungStd"),
        snapshotValue(verbrauchByKunde[kunde.id], "schulungKosten"),
        snapshotValue(verbrauchByKunde[kunde.id], "beratungStd"),
        snapshotValue(verbrauchByKunde[kunde.id], "beratungKosten"),
        abrechnungFormatBetrag(abrechnungNetto(verbrauchByKunde[kunde.id])),
        abrechnungFormatBetrag(abrechnungMwst(verbrauchByKunde[kunde.id])),
        abrechnungFormatBetrag(abrechnungBrutto(verbrauchByKunde[kunde.id]))
    )

    fun detailRowKey(row: All): String = when {
        row.ticketnummer.isNotBlank() -> "SUP:${row.id}"
        row.notiz.startsWith("AUTO_SERVICE_TERMIN:") -> "SRV:${row.id}"
        else -> "VRB:${row.id}"
    }

    fun detailRowsForTable(): List<Pair<String, All>> =
        selectedRows().mapIndexed { index, row ->
            detailRowKey(row) to row.copy(label = (index + 1).toString())
        }

    fun mapDetailRow(row: All): List<String> = listOf(
        row.label.ifBlank { row.id },
        row.datum,
        detailTypLabel(row),
        row.einmalzahlung,
        row.lizenzMonatlich,
        row.crSatz,
        if (row.ticketnummer.isNotBlank()) "1" else row.supportTicket,
        row.meetings,
        row.extraSchulungRemote,
        row.sonstiges
    )

    TabularSplitScreen(
        initialRatio = 0.56f,
        screen1 = {
            TabularScreen(
                screenKey = "Kundenabrechnung",
                rollenKey = "Kundenabrechnung",
                title = "Kundenabrechnung ($monthKey)",
                headers = listOf(
                    "ID", "Firmen-ID", "Firma", "Lizenzkey", "Abrechnungstag", "Monat",
                    "Onboarding Preis", "Lizensierung Preis",
                    "CR STD", "CR Kosten",
                    "SUP-Ticket", "Support Kosten",
                    "Meeting STD", "Meeting Kosten",
                    "Schulung STD", "Schulung Kosten",
                    "Beratung STD", "Beratung Kosten",
                    "Gesamt Netto", "MwSt. 19 %", "Gesamt Brutto"
                ),
                data = kunden.map { it.id to mapKundeRow(it) },
                editable = false,
                includeMetadata = true,
                allowCreate = false,
                allowDelete = false,
                onSearchWithFilters = { filters ->
                    scope.launch {
                        val rows = KundenverbrauchApi.searchAbrechnung(monthKey, filters)
                        kunden = rows
                        verbrauchByKunde = rows.associateBy { it.id }
                        selectedKundeId = rows.firstOrNull()?.id
                    }
                },
                onRowClick = { id -> selectedKundeId = id },
                onCommit = {}
            )
        },
        screen2 = selectedKundeId?.let {
            {
                val kunde = kunden.firstOrNull { it.id == selectedKundeId }
                if (kunde != null) {
                    TabularScreen(
                        screenKey = "Kundenabrechnung_${kunde.id}_$monthKey",
                        rollenKey = "Kundenabrechnung",
                        title = "Details ${kunde.firma.ifBlank { kunde.firmenId }}",
                        headers = listOf(
                            "ID", "Datum", "Typ",
                            "Onboarding", "Lizensierung",
                            "CR STD", "SUP-Ticket",
                            "Meetings", "Schulung", "Beratung"
                        ),
                        data = detailRowsForTable().map { (key, row) ->
                            key to mapDetailRow(row)
                        },
                        editable = false,
                        includeMetadata = true,
                        allowCreate = false,
                        allowDelete = false,
                        allowSave = false,
                        nonEditableCols = setOf("ID"),
                        onSearchWithFilters = { filters ->
                            scope.launch {
                                val details = KundenverbrauchApi.searchAbrechnungDetails(kunde.firmenId, monthKey, filters)
                                val current = verbrauchByKunde[kunde.id] ?: All(
                                    id = "${kunde.firmenId}_$monthKey",
                                    firmenId = kunde.firmenId,
                                    firma = kunde.firma,
                                    datum = monthKey
                                )
                                verbrauchByKunde = verbrauchByKunde + (kunde.id to current.copy(verbrauch = details))
                            }
                        },
                        onCommit = {}
                    )
                }
            }
        }
    )
}
